/**
 * Data layer for `agency`. Thin typed fetch wrappers over
 *   /api/v1/agency/clients — links a client workspace to this agency,
 *   lists linked client workspaces, and removes links.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export interface AgencyRow {
	id: string;
	client_tenant_id: string;
	client_slug?: string | null;
	client_name?: string | null;
	role?: "manager" | "viewer" | "billing";
	status?: "active" | "paused" | "revoked";
	created_at?: number;
	[key: string]: unknown;
}

export interface ListAgencyResponse {
	count: number;
	items: AgencyRow[];
}

export interface CreateAgencyBody {
	client_tenant_id?: string;
	client_slug?: string;
	role?: "manager" | "viewer" | "billing";
	// Scaffold form sends a free-text `name` field; treated as a client slug
	// request when neither client_tenant_id nor client_slug was provided.
	name?: string;
}

interface RawLinkedClientsResponse {
	active: string | null;
	clients: Array<{
		id: string;
		client_tenant_id: string;
		client_slug: string | null;
		client_name: string | null;
		role: "manager" | "viewer" | "billing";
		status: "active" | "paused" | "revoked";
		created_at: number;
	}>;
}

const BASE = "/api/v1/agency";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAgencyResponse> {
	const res = await fetch(`${BASE}/clients`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	const raw = await parseOrThrow<RawLinkedClientsResponse>(res);
	const items: AgencyRow[] = (raw?.clients ?? []).map((c) => ({
		id: c.id,
		client_tenant_id: c.client_tenant_id,
		client_slug: c.client_slug,
		client_name: c.client_name,
		role: c.role,
		status: c.status,
		created_at: c.created_at,
	}));
	return { count: items.length, items };
}

export async function create(body: CreateAgencyBody): Promise<AgencyRow> {
	const payload: Record<string, unknown> = {};
	if (body.client_tenant_id) payload.client_tenant_id = body.client_tenant_id;
	if (body.client_slug) payload.client_slug = body.client_slug;
	if (!payload.client_tenant_id && !payload.client_slug && body.name) {
		// Scaffold form sends `name`; treat it as a client slug request.
		payload.client_slug = body.name;
	}
	if (body.role) payload.role = body.role;

	const res = await fetch(`${BASE}/clients`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(payload),
	});
	return parseOrThrow<AgencyRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/clients/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	const parsed = await parseOrThrow<{ id?: string; unlinked?: boolean }>(res);
	return { removed: Boolean(parsed?.unlinked) };
}

export const agencyQueryKeys = {
	all: ["agency"] as const,
	list: () => [...agencyQueryKeys.all] as const,
	detail: (id: string) => [...agencyQueryKeys.all, id] as const,
};
